// ============================================================
// EntranceClass — migrates /admin-panel/entrance-class
// Angular source: EntranceClassComponent
//
// Features:
//   - DataTable with paginator showing entrance class teacher list
//   - "Add new" button opens Dialog form (district, block dropdowns, teacher ID verify)
//   - Edit (view) and Delete actions per row
//   - Delete confirmation dialog
//
// PrimeReact components used: DataTable, Column, Dialog, Dropdown,
//   InputText, Button, Toast
// ============================================================
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import districtData from '../../../assets/jsons/district.json';
import {
  useGetEntranceClassListMutation,
  useSaveEntranceClassTeacherMutation,
  useRemoveEntranceClassTeacherMutation,
  useLazyGetBlocksByDistrictQuery,
  useLazyVerifyTeacherQuery,
  EntranceClassRow,
  BlockItem,
} from '../AdminPanel.service';
import './entrance-class.css';

// ── Types ────────────────────────────────────────────────────

interface DropdownOption {
  label: string;
  value: number;
}

// ── Component ────────────────────────────────────────────────

const EntranceClass: React.FC = () => {
  const toast = useRef<Toast>(null);

  // ── Data state ──
  const [tableData, setTableData] = useState<EntranceClassRow[]>([]);

  // ── Dialog state ──
  const [displayDialog, setDisplayDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [submitShow, setSubmitShow] = useState(true);
  const [globalFilter, setGlobalFilter] = useState<string>('');
  // ── Form fields ──
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null);
  const [selectedBlock, setSelectedBlock] = useState<number | null>(null);
  const [teacherId, setTeacherId] = useState('');
  const [teacherName, setTeacherName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [schoolId, setSchoolId] = useState('');
  const [tchDataShow, setTchDataShow] = useState(false);
  const [tchVerified, setTchVerified] = useState(false);
  const [sclShow, setSclShow] = useState(false);
  const [formTouched, setFormTouched] = useState(false);

  // ── Dropdown options ──
  const [blockOptions, setBlockOptions] = useState<DropdownOption[]>([]);

  // ── Field disabled state (for edit mode) ──
  const [fieldsDisabled, setFieldsDisabled] = useState(false);

  // ── District options from imported JSON ──
  const districtOptions: DropdownOption[] = useMemo(
    () =>
      districtData
        .map((d) => ({ label: d.district_name, value: d.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    []
  );

  // ── RTK Query hooks ──
  const [fetchList, { isLoading: listLoading }] =
    useGetEntranceClassListMutation();
  const [saveTeacher, { isLoading: saveLoading }] =
    useSaveEntranceClassTeacherMutation();
  const [removeTeacher, { isLoading: removeLoading }] =
    useRemoveEntranceClassTeacherMutation();
  const [triggerBlocks] = useLazyGetBlocksByDistrictQuery();
  const [triggerVerify] = useLazyVerifyTeacherQuery();

  // ── Load table data on mount ──
  async function loadData() {
    const res = await fetchList({ Type: '1' }).unwrap();
    if (res.dataStatus) {
      setTableData(res.result ?? []);
    }
  }

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load blocks when district changes ──
  async function handleDistrictChange(distId: number, mode: 'new' | 'patch') {
    setSelectedDistrict(distId);
    setSelectedBlock(null);
    const res = await triggerBlocks(distId).unwrap();
    if (res.dataStatus) {
      let blocks: DropdownOption[] = (res.records ?? []).map(
        (b: BlockItem) => ({
          label: b.BlockName,
          value: b.BlockId,
        })
      );
      // In "new" mode, remove blocks already assigned in the same district
      if (mode === 'new') {
        const assignedBlocks = tableData
          .filter((r) => String(r.dist_id) === String(distId))
          .map((r) => Number(r.block_id));
        blocks = blocks.filter((b) => !assignedBlocks.includes(b.value));
      }
      setBlockOptions(blocks);
    } else {
      toast.current?.show({
        severity: 'error',
        detail: res.message,
        life: 3000,
      });
      setBlockOptions([]);
    }
  }

  // ── Verify teacher ID ──
  async function handleVerifyTeacher() {
    if (!teacherId || teacherId.length !== 8) {
      toast.current?.show({
        severity: 'error',
        detail: 'Enter a valid 8-digit Teacher ID',
        life: 3000,
      });
      return;
    }
    const res = await triggerVerify(teacherId).unwrap();
    if (res.dataStatus && res.result?.length > 0) {
      const t = res.result[0];
      setTeacherName(t.TchrName ?? '');
      setSchoolName(t.SchlName ?? '');
      setSchoolId(t.SchlId ?? '');
      setSclShow(!!t.SchlName);
      setTchDataShow(true);
      setTchVerified(true);
    } else {
      toast.current?.show({
        severity: 'error',
        detail: 'Please Enter Valid Teacher Id',
        life: 3000,
      });
      setTchVerified(false);
    }
  }

  // ── Save form ──
  async function handleSave() {
    setFormTouched(true);
    if (!selectedDistrict || !selectedBlock || !teacherId || !tchVerified)
      return;

    const data = {
      records: {
        id: '',
        type: '1',
        dist_id: selectedDistrict,
        block_id: selectedBlock,
        teacher_id: teacherId,
        school_id: schoolId,
      },
    };
    const res = await saveTeacher(data).unwrap();
    if (res.dataStatus) {
      toast.current?.show({
        severity: 'success',
        detail: res.message,
        life: 3000,
      });
      setDisplayDialog(false);
      loadData();
    } else {
      toast.current?.show({
        severity: 'error',
        detail: res.message,
        life: 3000,
      });
    }
  }

  // ── Delete ──
  async function handleDelete() {
    if (!deleteId) return;
    const res = await removeTeacher({ records: { IndxId: deleteId } }).unwrap();
    if (res.dataStatus) {
      toast.current?.show({
        severity: 'success',
        detail: res.message,
        life: 3000,
      });
      setDeleteDialog(false);
      loadData();
    } else {
      toast.current?.show({
        severity: 'error',
        detail: res.message,
        life: 3000,
      });
    }
  }

  // ── Open "Add new" dialog ──
  function openNewDialog() {
    resetForm();
    setFieldsDisabled(false);
    setSubmitShow(true);
    setDisplayDialog(true);
  }

  // ── Open "Edit/View" dialog ──
  function openEditDialog(row: EntranceClassRow) {
    resetForm();
    setFieldsDisabled(true);
    setSubmitShow(false);
    setDisplayDialog(true);
    setTchDataShow(true);
    setTchVerified(true);
    setSelectedDistrict(Number(row.dist_id));
    setSelectedBlock(Number(row.block_id));
    setTeacherId(row.teacher_id);
    setTeacherName(row.teacher_name);
    setSchoolName(row.school_name ?? '');
    setSclShow(!!row.school_name);
    handleDistrictChange(Number(row.dist_id), 'patch');
  }

  // ── Reset form helper ──
  function resetForm() {
    setSelectedDistrict(null);
    setSelectedBlock(null);
    setTeacherId('');
    setTeacherName('');
    setSchoolName('');
    setSchoolId('');
    setTchDataShow(false);
    setTchVerified(false);
    setSclShow(false);
    setBlockOptions([]);
    setFormTouched(false);
  }

  // ── Form validity ──
  const formValid =
    !!selectedDistrict &&
    !!selectedBlock &&
    teacherId.length === 8 &&
    tchVerified;

  // ── Action column body template ──
  const actionBody = (row: EntranceClassRow) => (
    <div className="d-flex justify-content-center gap-2">
      <Button
        icon="pi pi-eye"
        className="p-button-sm p-button-info"
        onClick={() => openEditDialog(row)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-sm p-button-danger"
        onClick={() => {
          setDeleteId(row.id);
          setDeleteDialog(true);
        }}
      />
    </div>
  );

  return (
    <div className="container-fluid py-3">
      <Toast ref={toast} />

      {/* Breadcrumb / heading */}
      <div className="entrance-heading mb-3">
        <h4>Orientation</h4>
      </div>

      {/* Main table card */}
      <div className="comparative-grid mt-3">
        <div className="mb-2">
          <input
            type="text"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="Search..."
            className="global-search-input"
          />
        </div>
        <div style={{ overflowX: 'auto' }}>
          <DataTable
            value={tableData}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50, 100]}
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"

            loading={listLoading}
            stripedRows
            showGridlines
            emptyMessage="No Records Found"
            globalFilter={globalFilter}
            globalFilterFields={[
              'district_name',
              'block_name',
              'teacher_id',
              'teacher_name',
              'udise_code',
              'school_name',
            ]}
            header={
              <div className="d-flex justify-content-end p-3">
                <Button
                  label="Add new"
                  icon="pi pi-plus"
                  className="solid-info-btn"
                  onClick={openNewDialog}
                />
              </div>
            }
          >
            <Column
              header="S.no"
              body={(_r, opts) => opts.rowIndex + 1}
              style={{ width: '4rem', textAlign: 'center' }}
            />
            <Column
              field="district_name"
              header="District"
              style={{ textAlign: 'center' }}
            />
            <Column
              field="block_name"
              header="Block"
              style={{ textAlign: 'center' }}
            />
            <Column
              field="teacher_id"
              header="Teacher Id"
              style={{ textAlign: 'center' }}
            />
            <Column
              field="teacher_name"
              header="Teacher Name"
              style={{ textAlign: 'center' }}
            />
            <Column
              header="Udise code"
              body={(r: EntranceClassRow) => r.udise_code ?? '-'}
              style={{ textAlign: 'center' }}
            />
            <Column
              header="School Name"
              body={(r: EntranceClassRow) => r.school_name ?? '-'}
              style={{ textAlign: 'center' }}
            />
            <Column
              header="Action"
              body={actionBody}
              style={{ textAlign: 'center', width: '8rem' }}
            />
          </DataTable>
        </div>
      </div>

      {/* ════════════ ADD/EDIT DIALOG ════════════ */}
      <Dialog
        header="Entrance Exam Coaching Centre"
        visible={displayDialog}
        style={{ width: '70vw' }}
        modal
        draggable={false}
        onHide={() => setDisplayDialog(false)}
      >
        <div className="p-3">
          <div className="row">
            {/* District */}
            <div className="col-lg-6 col-md-6 col-sm-12 mb-3">
              <label className="form-label">
                <b>District</b>
                <span className="text-danger">*</span>
              </label>
              <Dropdown
                value={selectedDistrict}
                options={districtOptions}
                onChange={(e) => handleDistrictChange(e.value, 'new')}
                placeholder="Select District"
                filter
                className="w-100"
                disabled={fieldsDisabled}
              />
              {formTouched && !selectedDistrict && (
                <small className="text-danger">Field is Required</small>
              )}
            </div>

            {/* Block */}
            <div className="col-lg-6 col-md-6 col-sm-12 mb-3">
              <label className="form-label">
                <b>Block</b>
                <span className="text-danger">*</span>
              </label>
              <Dropdown
                value={selectedBlock}
                options={blockOptions}
                onChange={(e) => setSelectedBlock(e.value)}
                placeholder="Select block"
                filter
                className="w-100"
                disabled={fieldsDisabled}
              />
              {formTouched && !selectedBlock && (
                <small className="text-danger">Field is Required</small>
              )}
            </div>

            {/* Teacher ID + Verify */}
            <div
              className={
                !tchVerified && teacherId.length === 8 && !fieldsDisabled
                  ? 'col-lg-6 col-md-6 col-sm-12 mb-3'
                  : 'col-lg-4 col-md-4 col-sm-12 mb-3'
              }
            >
              <label className="form-label">
                <b>Teacher Id</b>
                <span className="text-danger">*</span>
              </label>
              <div className="d-flex align-items-center gap-2">
                <InputText
                  value={teacherId}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 8);
                    setTeacherId(val);
                    setTchVerified(false);
                    setTchDataShow(false);
                  }}
                  placeholder="Enter Teacher Id"
                  maxLength={8}
                  disabled={fieldsDisabled}
                />
                {!tchVerified && teacherId.length === 8 && !fieldsDisabled && (
                  <Button
                    label="Verify"
                    className="solid-info-btn"
                    onClick={handleVerifyTeacher}
                  />
                )}
              </div>
              {formTouched && !teacherId && (
                <small className="text-danger">Field is Required</small>
              )}
              {formTouched && teacherId && teacherId.length !== 8 && (
                <small className="text-danger">
                  Only 8-digit numbers are allowed.
                </small>
              )}
              {formTouched && !tchVerified && teacherId.length === 8 && (
                <small className="text-danger">Please Verify Teacher</small>
              )}
            </div>

            {/* Teacher Name (shown after verify) */}
            {tchDataShow && (
              <div className="col-lg-4 col-md-4 col-sm-12 mb-3">
                <label className="form-label">
                  <b>Teacher Name</b>
                </label>
                <InputText value={teacherName} disabled className="w-100" />
              </div>
            )}

            {/* School Name (shown if school exists) */}
            {tchDataShow && sclShow && (
              <div className="col-lg-4 col-md-4 col-sm-12 mb-3">
                <label className="form-label">
                  <b>School</b>
                </label>
                <InputText
                  value={schoolName}
                  disabled
                  className="w-100"
                  title={schoolName}
                />
              </div>
            )}
          </div>

          {/* Save button (hidden in view mode) */}
          {submitShow && (
            <div className="text-end mt-3">
              <Button
                label="Save"
                className="btn btn-primary px-5 py-2"
                disabled={!formValid}
                loading={saveLoading}
                onClick={handleSave}
              />
            </div>
          )}
        </div>
      </Dialog>

      {/* ════════════ DELETE CONFIRMATION DIALOG ════════════ */}
      <Dialog
        header="Delete Member"
        visible={deleteDialog}
        modal
        closable={false}
        draggable={false}
        onHide={() => setDeleteDialog(false)}
        style={{ width: '30rem' }}
      >
        <div className="text-center py-3">
          <h4 className="mb-3">Are You sure?</h4>
          <h5>
            Do You really want to delete these records?
            <br />
            This process cannot be undone
          </h5>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button
              label="Cancel"
              className="solid-danger-btn px-5"
              onClick={() => setDeleteDialog(false)}
            />
            <Button
              label="Delete"
              className="solid-success-btn px-5"
              loading={removeLoading}
              onClick={handleDelete}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default EntranceClass;
